/**
 * One-Time Jobs List Component
 * Displays one-time jobs with assignment options for Company administrators
 */

import { useState, useEffect } from 'react';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../../config/firebase';
import type { OneTimeJob, UserProfile } from '../../../../shared/types';
import {
  assignOneTimeJobToManager,
  assignOneTimeJobToStaff,
  cancelOneTimeJob,
  unassignOneTimeJob,
  ONE_TIME_JOBS_COLLECTION,
} from '../../services/oneTimeJob.service';
import { getCompanyUsers } from '../../services/user.service';
import { convertDocumentTimestamps } from '@shared/utils/firestore';
import { ErrorMessage } from '../ErrorMessage';
import { EditOneTimeJobModal, CancelOneTimeJobModal } from '../modals';
import { ItemCard } from '../ItemCard';
import { ConfirmationDialog } from '../ConfirmationDialog';
import { Toast } from '../common';
import { useToast } from '../../hooks/useToast';
import { EMPTY_STATE_STYLE } from '../../styles/components';

interface OneTimeJobsListProps {
  companyId: string;
  refreshTrigger?: number;
  companyTimezone?: string;
}

export function OneTimeJobsList({ companyId, refreshTrigger, companyTimezone }: OneTimeJobsListProps) {
  const { toasts, dismissToast } = useToast();
  const [oneTimeJobs, setOneTimeJobs] = useState<OneTimeJob[]>([]);
  const [managers, setManagers] = useState<UserProfile[]>([]);
  const [staff, setStaff] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [noManagerConfirm, setNoManagerConfirm] = useState<{ jobId: string; staffId: string; name: string } | null>(null);
  const [assigningOneTimeJobId, setAssigningOneTimeJobId] = useState<string | null>(null);
  const [editingOneTimeJob, setEditingOneTimeJob] = useState<OneTimeJob | null>(null);
  const [cancellingOneTimeJob, setCancellingOneTimeJob] = useState<OneTimeJob | null>(null);

  // Load users once (they don't change as frequently)
  const loadUsers = async () => {
    try {
      const usersData = await getCompanyUsers(companyId);
      setManagers(usersData.filter((u: UserProfile) => u.userType === 'manager' && !u.isArchived));
      setStaff(usersData.filter((u: UserProfile) => u.userType === 'staff' && !u.isArchived));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    }
  };

  // Real-time listener for one-time jobs
  useEffect(() => {
    setLoading(true);
    setError('');

    // Load users
    loadUsers();

    // Set up real-time listener for one-time jobs
    const oneTimeJobsQuery = query(
      collection(db, ONE_TIME_JOBS_COLLECTION),
      where('companyId', '==', companyId),
      orderBy('scheduledStart', 'desc')
    );

    const unsubscribe = onSnapshot(
      oneTimeJobsQuery,
      (snapshot) => {
        const oneTimeJobsData = snapshot.docs
          .map((doc) => ({ id: doc.id, ...convertDocumentTimestamps<OneTimeJob>(doc.data()) }) as OneTimeJob)
          .filter((job) => !(job.status === 'cancelled' && job.isRecurringInstance));

        setOneTimeJobs(oneTimeJobsData);
        setLoading(false);
      },
      (err) => {
        setError(err.message || 'Failed to load one-time jobs');
        setLoading(false);
      }
    );

    // Cleanup listener on unmount
    return () => unsubscribe();
  }, [companyId, refreshTrigger]);

  const handleAssignToManager = async (oneTimeJobId: string, managerId: string) => {
    setAssigningOneTimeJobId(oneTimeJobId);
    try {
      await assignOneTimeJobToManager(oneTimeJobId, managerId);
      // Real-time listener will automatically update the UI
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to assign one-time job to manager');
    } finally {
      setAssigningOneTimeJobId(null);
    }
  };

  const handleAssignToStaff = async (oneTimeJobId: string, staffId: string) => {
    const hasManager = managers.some((m) => m.managedStaffIds?.includes(staffId));
    if (!hasManager) {
      const staffMember = staff.find((s) => s.uid === staffId);
      const name = staffMember ? `${staffMember.firstName} ${staffMember.lastName}` : 'This staff member';
      setNoManagerConfirm({ jobId: oneTimeJobId, staffId, name });
      return;
    }
    await doAssignToStaff(oneTimeJobId, staffId);
  };

  const doAssignToStaff = async (oneTimeJobId: string, staffId: string) => {
    setAssigningOneTimeJobId(oneTimeJobId);
    try {
      await assignOneTimeJobToStaff(oneTimeJobId, staffId);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to assign one-time job to staff');
    } finally {
      setAssigningOneTimeJobId(null);
    }
  };

  const handleUnassignOneTimeJob = async (oneTimeJobId: string) => {
    try {
      await unassignOneTimeJob(oneTimeJobId);
      // Real-time listener will automatically update the UI
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to unassign one-time job');
    }
  };

  const handleCancelOneTimeJob = async (oneTimeJobId: string) => {
    try {
      await cancelOneTimeJob(oneTimeJobId);
      setCancellingOneTimeJob(null);
      // Real-time listener will automatically update the UI
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to cancel one-time job');
      throw err; // Re-throw so modal can handle error
    }
  };

  // Helper to get user name from both managers and staff arrays
  const getUserName = (userId: string): string => {
    const allUsers = [...managers, ...staff];
    const user = allUsers.find(u => u.uid === userId);
    return user ? `${user.firstName} ${user.lastName}` : 'Unknown';
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>Loading one-time jobs...</div>;
  }

  return (
    <div>
      {error && <ErrorMessage message={error} />}

      {oneTimeJobs.length === 0 ? (
        <div style={EMPTY_STATE_STYLE}>
          No one-time jobs created yet. Click "Create One-Time Job" to get started.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {oneTimeJobs.map((oneTimeJob) => (
            <ItemCard
              key={oneTimeJob.id}
              item={oneTimeJob}
              viewerRole="company"
              managers={managers}
              staff={staff}
              onAssignToManager={handleAssignToManager}
              onAssignToStaff={handleAssignToStaff}
              onUnassign={handleUnassignOneTimeJob}
              onEditOneTimeJob={setEditingOneTimeJob}
              onCancelOneTimeJob={setCancellingOneTimeJob}
              isAssigning={assigningOneTimeJobId === oneTimeJob.id}
              getUserName={getUserName}
              companyTimezone={companyTimezone}
            />
          ))}
        </div>
      )}

      {/* Edit One-Time Job Modal */}
      <EditOneTimeJobModal
        isOpen={!!editingOneTimeJob}
        onClose={() => setEditingOneTimeJob(null)}
        oneTimeJob={editingOneTimeJob}
        onSuccess={() => {
          setEditingOneTimeJob(null);
          // Real-time listener will automatically update
        }}
      />

      {/* Cancel One-Time Job Modal */}
      {cancellingOneTimeJob && (
        <CancelOneTimeJobModal
          oneTimeJob={cancellingOneTimeJob}
          onClose={() => setCancellingOneTimeJob(null)}
          onCancel={handleCancelOneTimeJob}
          assignedManagerName={cancellingOneTimeJob.assignedManagerId ? getUserName(cancellingOneTimeJob.assignedManagerId) : undefined}
          assignedStaffName={cancellingOneTimeJob.userIds?.length ? cancellingOneTimeJob.userIds.map(getUserName).join(', ') : undefined}
          companyTimezone={companyTimezone}
        />
      )}

      <ConfirmationDialog
        isOpen={!!noManagerConfirm}
        title="No Manager Assigned"
        message={`${noManagerConfirm?.name} has no assigned manager. Time entries will only be visible to the company administrator — no manager will receive alerts or be able to approve their hours.`}
        confirmLabel="Assign Anyway"
        confirmStyle="warning"
        onConfirm={async () => {
          if (noManagerConfirm) {
            setNoManagerConfirm(null);
            await doAssignToStaff(noManagerConfirm.jobId, noManagerConfirm.staffId);
          }
        }}
        onCancel={() => setNoManagerConfirm(null)}
      />

      <Toast toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
}
